import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '@/components/advizeo-modal-spawner/advizeo-modal-spawner.component';
import type { UniverseId } from '@/domain/universes/universe.interface';
import { ErrorHandlingProvider } from '@/providers/error-handling/error-handling.provider';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { UiFormErrorComponent } from '@/ui/form-field/form-error/ui-form-error';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { observeControlValidity$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { ReferenceListLabelPipe } from '@/utils/reference-lists/pipes/reference-list-label.pipe';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, type OnInit, inject, output, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import type { AlertScenarioId } from '../../alerts.interface';
import { DriftScenarioService } from '../drift-scenario-service/drift-scenario.service';
import type { DriftMeasureLevel, DriftTargetMeasure } from '../drift-scenario.interface';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'drift-scenario-creation-modal',
    templateUrl: './drift-scenario-creation-modal.component.html',
    styleUrls: ['./drift-scenario-creation-modal.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UiFormFieldComponent,
        AdvizeoInputDirective,
        UiFormErrorComponent,
        UiSelectComponent,
        AsyncPipe,
        UpperCasePipe,
        TranslatePipe,
        ReferenceListLabelPipe
    ]
})
export class DriftScenarioCreationModalComponent implements AdvizeoModalComponent<{}, AlertScenarioId | null>, OnInit {
    private _fb = inject(FormBuilder);
    private _driftScenarioService = inject(DriftScenarioService);
    private _translateService = inject(TranslateService);
    private _errorHandlingProvider = inject(ErrorHandlingProvider);

    title = signal('');
    isDismissable = signal(true);
    buttons = signal<AdvizeoModalButton[]>([]);
    close = output<AlertScenarioId | void>();
    input = injectAdvizeoModalInput<{}>();

    form = this._fb.group({
        name: this._fb.nonNullable.control('', Validators.required),
        universeId: this._fb.control<UniverseId | null>(null, Validators.required),
        targetMeasure: this._fb.control<DriftTargetMeasure | null>(null, Validators.required),
        measureLevel: this._fb.control<DriftMeasureLevel | null>(null, Validators.required)
    });

    universeIds$ = this._driftScenarioService.getAvailableDriftUniverseIds$();
    measureLevels$ = this._driftScenarioService.getAvailableMeasureLevels$();
    targetMeasures$ = this._driftScenarioService.getAvailableTargetMeasures$();

    ngOnInit() {
        this._translateService
            .stream('PAGES.ALERT_SCENARIO.CREATE_DRIFT_SCENARIO')
            .pipe(untilDestroyed(this))
            .subscribe((title: string) => {
                this.title.set(title);
            });

        observeControlValidity$(this.form)
            .pipe(untilDestroyed(this))
            .subscribe(isValid => {
                this.buttons.set([
                    {
                        text: this._translateService.instant('BUTTONS.VALIDATE'),
                        onClick: () => {
                            this.createScenario();
                        },
                        isDisabled: !isValid,
                        color: SharedUiColorNameV2.PRIMARY
                    }
                ]);
            });
    }

    async createScenario() {
        if (!this.form.valid) {
            return;
        }
        const formValue = this.form.getRawValue();
        try {
            const alertScenarioId = await this._driftScenarioService.createDriftAlertScenario({
                name: formValue.name,
                universeId: formValue.universeId!,
                measureLevel: formValue.measureLevel!,
                targetMeasure: formValue.targetMeasure!
            });
            this.close.emit(alertScenarioId);
        } catch (err) {
            const errors = this._driftScenarioService.extractErrorsFromApiFailure(
                this._errorHandlingProvider.getErrorCodeFromApiResponse(err),
                this._errorHandlingProvider.getErrorPayloadFromApiResponse(err)
            );
            this._driftScenarioService.showErrors(errors);
        }
    }
}
